import { BRAND_IDS, normalizeBrandId, quotationDefaultCurrency } from '@nadoutong/contracts';
import type {
  BrandId,
  CrmQuotationSummary,
  QuotationCatalogResponse,
  QuotationCurrency,
  QuotationNamespace,
  QuotationSku,
} from '@nadoutong/contracts';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { OpsApiError } from '../../../api/client.ts';
import {
  getSavedQuotation,
  useQuotationCatalog,
  useSavedQuotations,
  useUpsertSavedQuotation,
} from '../../../api/quotation.ts';
import { useSession } from '../../../hooks/useSession.tsx';
import { useT } from '../../../i18n/index.ts';
import {
  Button,
  Checkbox,
  Chip,
  EmptyState,
  FormField,
  IconButton,
  InfoHint,
  Input,
  Panel,
  RadioGroup,
  Select,
  Table,
  TBody,
  Td,
  Th,
  THead,
  Tr,
} from '../../../ui/index.ts';
import type { SelectOption } from '../../../ui/index.ts';
import {
  draftStorageKey,
  effectiveSkuZhe,
  emptyDraft,
  loadDraft,
  namespaceBelowFloor,
  namespaceDraft,
  namespaceFloorZhe,
  namespaceOverrideCount,
  parseZhe,
  saveDraft,
  skuBelowFloor,
  skuDraft,
  skuFloorZhe,
} from './quotationDraft.ts';
import type { QuotationDraft, QuotationNamespaceDraft } from './quotationDraft.ts';
import {
  buildQuotationSheets,
  downloadQuotationXlsx,
  quotationFilename,
} from './quotationExport.ts';

interface Props {
  readonly isAdmin: boolean;
  readonly adminBrand: string;
  readonly opsUserId: string;
}

function todayIso(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

function browserStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  return window.localStorage;
}

const QUOTE_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

function knownBrand(value: string): BrandId | null {
  return normalizeBrandId(value);
}

export function QuotationTab({ isAdmin, adminBrand, opsUserId }: Props): JSX.Element {
  const t = useT();
  const [brand, setBrand] = useState<string>(isAdmin && adminBrand.length > 0 ? adminBrand : '');
  const queryEnabled = !isAdmin || brand.length > 0;
  const queryBrand = isAdmin ? brand : null;
  const catalog = useQuotationCatalog(queryBrand, queryEnabled);
  const catalogBrand = catalog.data?.brand ?? null;
  const draftBrand = isAdmin ? knownBrand(brand) : catalogBrand;
  const storageKey = draftStorageKey(opsUserId, queryBrand);
  const [draft, setDraft] = useState<QuotationDraft>(() => emptyDraft(todayIso(), 'CNY'));
  const [hydratedKey, setHydratedKey] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportFailed, setExportFailed] = useState(false);
  const [savedLine, setSavedLine] = useState<{
    readonly customer: string;
    readonly count: number;
  } | null>(null);
  const [saveFailed, setSaveFailed] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saveSkipped, setSaveSkipped] = useState(false);
  const [selectedSavedId, setSelectedSavedId] = useState<string | undefined>(undefined);
  const loadRequest = useRef(0);
  const saved = useSavedQuotations(queryBrand, queryEnabled);
  const upsert = useUpsertSavedQuotation();
  const { fetchImpl } = useSession();

  useEffect(() => {
    if (draftBrand === null) return;
    const storage = browserStorage();
    const loaded = storage === null ? null : loadDraft(storage, storageKey, draftBrand);
    setDraft(loaded ?? emptyDraft(todayIso(), quotationDefaultCurrency(draftBrand)));
    setHydratedKey(storageKey);
    setSavedLine(null);
    setSaveFailed(false);
    setSaveSkipped(false);
    setLoadFailed(false);
    setSelectedSavedId(undefined);
  }, [draftBrand, storageKey]);

  useEffect(() => {
    if (hydratedKey !== storageKey) return;
    const storage = browserStorage();
    if (storage === null) return;
    saveDraft(storage, storageKey, draft);
  }, [draft, hydratedKey, storageKey]);

  const brandOptions = useMemo(() => BRAND_IDS.map((id) => ({ value: id, label: id })), []);

  const savedRows = saved.data?.quotations;
  const savedOptions = useMemo<readonly SelectOption[]>(
    () =>
      (savedRows ?? []).map((row: CrmQuotationSummary) => ({
        value: row.id,
        label: `${row.customer_name} · ${row.quote_date}`,
      })),
    [savedRows],
  );

  function patchNamespace(slug: string, patch: Partial<QuotationNamespaceDraft>): void {
    setDraft((current) => {
      const previous = namespaceDraft(current, slug);
      return {
        ...current,
        namespaces: {
          ...current.namespaces,
          [slug]: { ...previous, ...patch },
        },
      };
    });
  }

  function patchSku(slug: string, skuId: string, discountZhe: string): void {
    setDraft((current) => {
      const previous = namespaceDraft(current, slug);
      const skus = { ...previous.skus };
      if (discountZhe.trim().length === 0) {
        delete skus[skuId];
      } else {
        skus[skuId] = { discount_zhe: discountZhe };
      }
      return {
        ...current,
        namespaces: {
          ...current.namespaces,
          [slug]: { ...previous, skus },
        },
      };
    });
  }

  function clearDraft(): void {
    loadRequest.current += 1;
    const currency = draftBrand === null ? 'CNY' : quotationDefaultCurrency(draftBrand);
    setDraft(emptyDraft(todayIso(), currency));
    setSelectedSavedId(undefined);
    setSavedLine(null);
    setSaveFailed(false);
    setSaveSkipped(false);
    setLoadFailed(false);
  }

  function draftCanSave(candidate: QuotationDraft): boolean {
    return (
      candidate.customer_name.trim().length > 0 && QUOTE_DATE_PATTERN.test(candidate.quote_date)
    );
  }

  async function exportWorkbook(): Promise<void> {
    if (catalog.data === undefined) return;
    setExporting(true);
    setExportFailed(false);
    setSavedLine(null);
    setSaveFailed(false);
    setSaveSkipped(false);
    try {
      const sheets = buildQuotationSheets(catalog.data, draft);
      await downloadQuotationXlsx(sheets, quotationFilename(draft));
    } catch {
      setExportFailed(true);
      setExporting(false);
      return;
    }
    if (!draftCanSave(draft)) {
      setSaveSkipped(true);
      setExporting(false);
      return;
    }
    try {
      const result = await upsert.mutateAsync({ brand: queryBrand, draft });
      setSavedLine({
        customer: result.quotation.customer_name,
        count: result.quotation.export_count,
      });
    } catch {
      setSaveFailed(true);
    } finally {
      setExporting(false);
    }
  }

  async function loadSaved(id: string): Promise<void> {
    const request = loadRequest.current + 1;
    loadRequest.current = request;
    setSelectedSavedId(id);
    setLoadFailed(false);
    try {
      const result = await getSavedQuotation(fetchImpl, id, queryBrand);
      if (loadRequest.current !== request) return;
      setDraft(result.quotation.draft);
      setSelectedSavedId('');
    } catch {
      if (loadRequest.current !== request) return;
      setLoadFailed(true);
    }
  }

  const canExport =
    catalog.data !== undefined &&
    catalog.data.namespaces.some(
      (namespace) => namespaceDraft(draft, namespace.namespace_slug).included,
    );

  return (
    <div className="flex flex-col gap-4" data-testid="quotation-tab">
      {isAdmin ? (
        <FormField
          label={t('customer_manager_workspace.quotation.brand')}
          fieldId="quotation-brand"
        >
          <Select
            id="quotation-brand"
            value={brand.length === 0 ? undefined : brand}
            onValueChange={setBrand}
            options={brandOptions}
            placeholder={t('customer_manager_workspace.quotation.brand_placeholder')}
            testId="quotation-brand"
          />
        </FormField>
      ) : null}

      {queryEnabled && (saved.isPending || (savedRows !== undefined && savedRows.length > 0)) ? (
        <FormField
          label={t('customer_manager_workspace.quotation.saved_label')}
          fieldId="quotation-saved"
        >
          <Select
            id="quotation-saved"
            value={selectedSavedId}
            onValueChange={(id) => void loadSaved(id)}
            options={savedOptions}
            placeholder={t('customer_manager_workspace.quotation.saved_placeholder')}
            disabled={saved.isPending}
            testId="quotation-saved-select"
          />
        </FormField>
      ) : null}
      {loadFailed ? (
        <span className="text-label text-bad" role="alert" data-testid="quotation-saved-error">
          {t('customer_manager_workspace.quotation.saved_load_failed')}
        </span>
      ) : null}

      <form
        className="grid grid-cols-1 gap-3 md:grid-cols-2"
        onSubmit={(event: FormEvent<HTMLFormElement>) => event.preventDefault()}
      >
        <FormField
          label={t('customer_manager_workspace.quotation.customer_name')}
          fieldId="quotation-customer-name"
        >
          <Input
            id="quotation-customer-name"
            value={draft.customer_name}
            onChange={(event) => {
              const customerName = event.currentTarget.value;
              setDraft((current) => ({ ...current, customer_name: customerName }));
            }}
            data-testid="quotation-customer-name"
          />
        </FormField>
        <FormField
          label={t('customer_manager_workspace.quotation.quote_date')}
          fieldId="quotation-date"
        >
          <Input
            id="quotation-date"
            type="date"
            value={draft.quote_date}
            onChange={(event) => {
              const quoteDate = event.currentTarget.value;
              setDraft((current) => ({ ...current, quote_date: quoteDate }));
            }}
            data-testid="quotation-date"
          />
        </FormField>
        <FormField
          label={t('customer_manager_workspace.quotation.valid_days')}
          fieldId="quotation-valid-days"
        >
          <Input
            id="quotation-valid-days"
            type="number"
            min={1}
            step={1}
            value={draft.valid_days}
            onChange={(event) => {
              const validDays = event.currentTarget.value;
              setDraft((current) => ({ ...current, valid_days: validDays }));
            }}
            data-testid="quotation-valid-days"
          />
        </FormField>
        <FormField label={t('customer_manager_workspace.quotation.note')} fieldId="quotation-note">
          <Input
            id="quotation-note"
            value={draft.note}
            onChange={(event) => {
              const note = event.currentTarget.value;
              setDraft((current) => ({ ...current, note }));
            }}
            data-testid="quotation-note"
          />
        </FormField>
        <FormField
          label={t('customer_manager_workspace.quotation.currency')}
          fieldId="quotation-currency"
        >
          <RadioGroup
            value={draft.currency}
            onValueChange={(value) => {
              if (value !== 'CNY' && value !== 'USD') return;
              const currency: QuotationCurrency = value;
              setDraft((current) => ({ ...current, currency }));
            }}
            options={[
              {
                value: 'CNY',
                label: t('customer_manager_workspace.quotation.currency_cny'),
              },
              {
                value: 'USD',
                label: t('customer_manager_workspace.quotation.currency_usd'),
              },
            ]}
            ariaLabel={t('customer_manager_workspace.quotation.currency')}
            orientation="horizontal"
            testId="quotation-currency"
          />
        </FormField>
      </form>

      {isAdmin && brand.length === 0 ? (
        <Panel caption={t('customer_manager_workspace.quotation.namespaces')} padded>
          <EmptyState
            title={t('customer_manager_workspace.quotation.pick_brand')}
            testId="quotation-pick-brand"
          />
        </Panel>
      ) : catalog.isPending ? (
        <div
          data-testid="quotation-loading"
          className="h-24 animate-pulse rounded-lg border border-line-soft bg-raised"
          aria-hidden="true"
        />
      ) : catalog.isError ? (
        <QuotationError error={catalog.error} onRetry={() => void catalog.refetch()} />
      ) : catalog.data.namespaces.length === 0 ? (
        <Panel caption={t('customer_manager_workspace.quotation.namespaces')} padded>
          <EmptyState
            title={t('customer_manager_workspace.quotation.empty')}
            testId="quotation-empty"
          />
        </Panel>
      ) : (
        <NamespaceTable
          catalog={catalog.data}
          draft={draft}
          onPatch={patchNamespace}
          onPatchSku={patchSku}
        />
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="primary"
          disabled={!canExport}
          loading={exporting}
          onClick={() => void exportWorkbook()}
          data-testid="quotation-export"
        >
          {t('customer_manager_workspace.quotation.export')}
        </Button>
        <Button variant="ghost" onClick={clearDraft} data-testid="quotation-clear">
          {t('customer_manager_workspace.quotation.clear')}
        </Button>
        {exportFailed ? (
          <span className="text-label text-bad" role="alert" data-testid="quotation-export-error">
            {t('customer_manager_workspace.quotation.export_failed')}
          </span>
        ) : null}
        {savedLine !== null ? (
          <span className="text-label text-paper" data-testid="quotation-saved-status">
            {t('customer_manager_workspace.quotation.saved_status', {
              customer: savedLine.customer,
              count: savedLine.count,
            })}
          </span>
        ) : null}
        {saveFailed ? (
          <span className="text-label text-bad" role="alert" data-testid="quotation-save-error">
            {t('customer_manager_workspace.quotation.save_failed')}
          </span>
        ) : null}
        {saveSkipped ? (
          <span className="text-label text-paper" data-testid="quotation-save-skipped">
            {t('customer_manager_workspace.quotation.save_skipped')}
          </span>
        ) : null}
      </div>
    </div>
  );
}

function NamespaceTable({
  catalog,
  draft,
  onPatch,
  onPatchSku,
}: {
  readonly catalog: QuotationCatalogResponse;
  readonly draft: QuotationDraft;
  readonly onPatch: (slug: string, patch: Partial<QuotationNamespaceDraft>) => void;
  readonly onPatchSku: (slug: string, skuId: string, discountZhe: string) => void;
}): JSX.Element {
  const t = useT();
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set());

  function toggleExpanded(slug: string): void {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  }

  return (
    <Table testId="quotation-namespaces">
      <THead>
        <Tr interactive={false}>
          <Th>{t('customer_manager_workspace.quotation.col.include')}</Th>
          <Th>{t('customer_manager_workspace.quotation.col.namespace')}</Th>
          <Th num>{t('customer_manager_workspace.quotation.col.sku_count')}</Th>
          <Th>
            <span className="inline-flex items-center gap-1">
              {t('customer_manager_workspace.quotation.col.floor')}
              <InfoHint
                content={t('customer_manager_workspace.quotation.floor_hint_excel')}
                label={t('customer_manager_workspace.quotation.floor_hint_excel')}
                testId="quotation-floor-excel-hint"
              />
            </span>
          </Th>
          <Th>{t('customer_manager_workspace.quotation.col.discount')}</Th>
          <Th>{t('customer_manager_workspace.quotation.col.status')}</Th>
        </Tr>
      </THead>
      <TBody>
        {catalog.namespaces.map((namespace) => (
          <NamespaceRow
            key={namespace.namespace_slug}
            namespace={namespace}
            entry={namespaceDraft(draft, namespace.namespace_slug)}
            expanded={expanded.has(namespace.namespace_slug)}
            onToggle={() => toggleExpanded(namespace.namespace_slug)}
            onPatch={onPatch}
            onPatchSku={onPatchSku}
          />
        ))}
      </TBody>
    </Table>
  );
}

function NamespaceRow({
  namespace,
  entry,
  expanded,
  onToggle,
  onPatch,
  onPatchSku,
}: {
  readonly namespace: QuotationNamespace;
  readonly entry: QuotationNamespaceDraft;
  readonly expanded: boolean;
  readonly onToggle: () => void;
  readonly onPatch: (slug: string, patch: Partial<QuotationNamespaceDraft>) => void;
  readonly onPatchSku: (slug: string, skuId: string, discountZhe: string) => void;
}): JSX.Element {
  const t = useT();
  const slug = namespace.namespace_slug;
  const zhe = parseZhe(entry.discount_zhe);
  const invalid = entry.discount_zhe.trim().length > 0 && zhe === null;
  const warn = namespaceBelowFloor(namespace, entry);
  const overrideCount = namespaceOverrideCount(entry);
  const floorValue = namespaceFloorZhe(namespace);
  const floor =
    floorValue === null
      ? t('customer_manager_workspace.quotation.floor_unset')
      : t('customer_manager_workspace.quotation.floor_hint', {
          zhe: floorValue.toFixed(1),
        });
  const floorHint = namespace.has_unconfigured_sales_cost
    ? `${floor} · ${t('customer_manager_workspace.quotation.floor_partial')}`
    : floor;

  return (
    <>
      <Tr interactive={false} data-testid={`quotation-ns-${slug}`}>
        <Td>
          <div className="flex items-center gap-2">
            <IconButton
              size="sm"
              label={
                expanded
                  ? t('customer_manager_workspace.quotation.collapse')
                  : t('customer_manager_workspace.quotation.expand')
              }
              onClick={onToggle}
              aria-expanded={expanded}
              data-testid={`quotation-ns-${slug}-expand`}
            >
              {expanded ? (
                <ChevronDown aria-hidden="true" className="h-3.5 w-3.5" />
              ) : (
                <ChevronRight aria-hidden="true" className="h-3.5 w-3.5" />
              )}
            </IconButton>
            <Checkbox
              checked={entry.included}
              onCheckedChange={(checked) => onPatch(slug, { included: checked })}
              label={t('customer_manager_workspace.quotation.include')}
              testId={`quotation-ns-${slug}-include`}
            />
          </div>
        </Td>
        <Td>
          <div className="flex flex-col gap-0.5">
            <span className="text-body text-paper">{namespace.namespace_display_name}</span>
            <span className="font-mono text-micro text-paper-low">{slug}</span>
          </div>
        </Td>
        <Td num>{namespace.skus.length}</Td>
        <Td>{floorHint}</Td>
        <Td>
          <Input
            mono
            compact
            type="number"
            step="0.1"
            min={0.1}
            max={10}
            value={entry.discount_zhe}
            invalid={invalid}
            aria-label={t('customer_manager_workspace.quotation.col.discount')}
            onChange={(event) => onPatch(slug, { discount_zhe: event.currentTarget.value })}
            wrapperClassName="w-24"
            data-testid={`quotation-ns-${slug}-discount`}
          />
        </Td>
        <Td>
          {warn ? (
            <Chip tone="warn" testId={`quotation-ns-${slug}-floor-warning`}>
              {t('customer_manager_workspace.quotation.below_floor')}
            </Chip>
          ) : null}
          {overrideCount > 0 ? (
            <Chip tone="signal" testId={`quotation-ns-${slug}-overrides`}>
              {t('customer_manager_workspace.quotation.overrides_count', { count: overrideCount })}
            </Chip>
          ) : null}
          {invalid ? (
            <span className="text-label text-bad" data-testid={`quotation-ns-${slug}-invalid`}>
              {t('customer_manager_workspace.quotation.discount_invalid')}
            </span>
          ) : null}
        </Td>
      </Tr>
      {expanded
        ? namespace.skus.map((sku) => (
            <SkuRow
              key={sku.sku_id}
              sku={sku}
              namespace={namespace}
              entry={entry}
              onPatchSku={onPatchSku}
            />
          ))
        : null}
    </>
  );
}

function SkuRow({
  sku,
  namespace,
  entry,
  onPatchSku,
}: {
  readonly sku: QuotationSku;
  readonly namespace: QuotationNamespace;
  readonly entry: QuotationNamespaceDraft;
  readonly onPatchSku: (slug: string, skuId: string, discountZhe: string) => void;
}): JSX.Element {
  const t = useT();
  const override = skuDraft(entry, sku.sku_id);
  const overrideText = override.discount_zhe;
  const parsedOverride = parseZhe(overrideText);
  const invalid = overrideText.trim().length > 0 && parsedOverride === null;
  const effective = effectiveSkuZhe(entry, sku.sku_id);
  const warn =
    effective !== null && skuBelowFloor(sku, namespace.sales_floor_multiplier, effective);
  const floorValue = skuFloorZhe(sku, namespace.sales_floor_multiplier);
  const floor =
    floorValue === null
      ? t('customer_manager_workspace.quotation.floor_unset')
      : t('customer_manager_workspace.quotation.floor_hint', {
          zhe: floorValue.toFixed(1),
        });
  const modelName = sku.model_display_name ?? sku.model;
  const categoryLabel =
    sku.category === 'text'
      ? t('customer_manager_workspace.quotation.category.text')
      : sku.category === 'image'
        ? t('customer_manager_workspace.quotation.category.image')
        : t('customer_manager_workspace.quotation.category.video');

  return (
    <Tr interactive={false} data-testid={`quotation-sku-${sku.sku_id}`}>
      <Td />
      <Td>
        <div className="flex flex-col gap-0.5">
          <span className="inline-flex items-center gap-2 text-body text-paper">
            {modelName}
            <Chip tone="neutral" lamp={false}>
              {categoryLabel}
            </Chip>
          </span>
          <span className="font-mono text-micro text-paper-low">{sku.sku_name}</span>
        </div>
      </Td>
      <Td num />
      <Td>{floor}</Td>
      <Td>
        <Input
          mono
          compact
          type="number"
          step="0.1"
          min={0.1}
          max={10}
          value={overrideText}
          placeholder={
            entry.discount_zhe.trim().length > 0
              ? t('customer_manager_workspace.quotation.inherit_placeholder', {
                  zhe: entry.discount_zhe.trim(),
                })
              : undefined
          }
          invalid={invalid}
          aria-label={t('customer_manager_workspace.quotation.col.sku_discount')}
          onChange={(event) => {
            const discountZhe = event.currentTarget.value;
            onPatchSku(namespace.namespace_slug, sku.sku_id, discountZhe);
          }}
          wrapperClassName="w-24"
          data-testid={`quotation-sku-${sku.sku_id}-discount`}
        />
      </Td>
      <Td>
        {warn ? (
          <Chip tone="warn" testId={`quotation-sku-${sku.sku_id}-floor-warning`}>
            {t('customer_manager_workspace.quotation.below_floor')}
          </Chip>
        ) : null}
        {invalid ? (
          <span className="text-label text-bad" data-testid={`quotation-sku-${sku.sku_id}-invalid`}>
            {t('customer_manager_workspace.quotation.discount_invalid')}
          </span>
        ) : null}
      </Td>
    </Tr>
  );
}

function QuotationError({
  error,
  onRetry,
}: {
  readonly error: Error;
  readonly onRetry: () => void;
}): JSX.Element {
  const t = useT();
  const detail =
    error instanceof OpsApiError ? `${error.code}: ${error.message}` : t('common.network_error');
  return (
    <div
      role="alert"
      data-testid="quotation-error"
      className="flex items-center justify-between gap-4 rounded-lg border border-bad-line bg-bad-dim/50 px-4 py-3 max-md:flex-wrap max-md:gap-3"
    >
      <div className="flex flex-col gap-1">
        <span className="text-body font-medium text-bad-bright">
          {t('customer_manager_workspace.quotation.error')}
        </span>
        <span className="text-dense text-bad">{detail}</span>
      </div>
      <Button variant="danger" size="sm" onClick={onRetry}>
        {t('common.retry')}
      </Button>
    </div>
  );
}
